import { invoke, isTauri } from '@tauri-apps/api/core';
import type { AppSettings, TaxRoundingMode } from '../types';
import { calcTax, type TaxSummary } from './reportCore';
import { calculateRoundedRatioDistributions } from './settlement';

export function isDesktopRuntime(): boolean {
  return isTauri();
}

export async function calculateTaxWithEngine(
  netAmount: number,
  isIndividual: boolean,
  settings: AppSettings
): Promise<TaxSummary> {
  if (!isDesktopRuntime()) {
    return calcTax(netAmount, isIndividual, settings);
  }

  return invoke<TaxSummary>('calculate_tax', {
    input: {
      netAmount,
      isIndividual,
      consumptionTaxRounding: settings.consumption_tax_rounding,
      withholdingTaxRounding: settings.withholding_tax_rounding,
    },
  });
}

interface RatioDistributionMemberInput {
  id: string;
  type?: string;
  ratio?: number;
  basis?: string;
  rounding_mode?: TaxRoundingMode;
  rounding_adjustment_target?: boolean;
}

interface RatioDistributionOutput {
  memberId: string;
  amount: number;
}

export async function calculateRatioDistributionsWithEngine<T extends RatioDistributionMemberInput>(
  members: T[],
  gross: number,
  distributable: number
): Promise<Map<string, number>> {
  if (!isDesktopRuntime()) {
    const fallback = calculateRoundedRatioDistributions(members, gross, distributable);
    return new Map(members.map(member => [member.id, fallback.get(member) ?? 0]));
  }

  const results = await invoke<RatioDistributionOutput[]>('calculate_ratio_distributions', {
    input: {
      gross,
      distributable,
      members: members.map(member => ({
        id: member.id,
        memberType: member.type ?? 'ratio',
        ratio: member.ratio ?? 0,
        basis: member.basis ?? 'net',
        roundingMode: member.rounding_mode ?? 'floor',
        roundingAdjustmentTarget: member.rounding_adjustment_target ?? false,
      })),
    },
  });

  return new Map(results.map(result => [result.memberId, result.amount]));
}
